import 'core-js/stable'
import 'regenerator-runtime/runtime'
import 'url-search-params-polyfill'

// Plugins
import 'floating-vue/dist/style.css'

import type { Plugin } from 'vue'

// Common utilities & mixins
import { setAccessToken, setupJsCommon } from '@internetworkexpert/js-common'
import {
  initializeFederatedLoginCallback,
  replaceFederatedLoginCallbackHistory,
} from '@/helpers/checkoutLogin/federatedLoginCallback'

declare global {
  interface Window {
    dataLayer: any
  }
}

// 1) Setup common helpers and consume any OAuth callback before router or
// third-party tracking can observe its fragment.
setupJsCommon(process.env.VUE_APP_ENVIRONMENT || 'production')

if (typeof window !== 'undefined') {
  initializeFederatedLoginCallback({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    setAccessToken,
    replaceUrl: replaceFederatedLoginCallbackHistory,
  })
}

// Static imports are evaluated before this module body. Load every module that
// can construct router history or observe the URL only after callback cleanup.
export const applicationBootstrap = Promise.all([
  import('vue'),

  // Plugins
  import('floating-vue'),

  // Bugsnag
  import('@bugsnag/js'),
  import('@bugsnag/plugin-vue'),

  // Common utilities & mixins
  import('@/helpers/mixins/mixpanelEvent'),
  import('@/helpers/thirdPartyLoaders'),

  // Custom directive
  import('@/components/shared/followTooltipDirective'),
  import('@/store'),
  import('@/App.vue'),
  import('@/router'),
]).then(
  ([
    { createApp },
    { VTooltip },
    { default: Bugsnag },
    { default: BugsnagPluginVue },
    { mixpanelEvent },
    { initializeDeferredThirdPartyIntegrations },
    { default: followTooltip },
    { default: store },
    { default: App },
    { default: router },
  ]) => {
    // 2) Initialize Bugsnag before mounting.
    Bugsnag.start({
      apiKey: process.env.VUE_APP_BUGSNAG_KEY || '',
      releaseStage: process.env.VUE_APP_ENVIRONMENT,
      plugins: [new BugsnagPluginVue()],
      autoDetectErrors: false,
      enabledReleaseStages: ['production', 'staging', 'development'],
      appVersion: process.env.VUE_APP_VERSION,
    })
    const bugsnagVue = Bugsnag.getPlugin('vue')

    // GA4/Ads via GTM: ensure dataLayer exists (no UA/gtag usage here)
    window.dataLayer = window.dataLayer || []
    initializeDeferredThirdPartyIntegrations()

    // 3) Create the Vue 3 application
    const app = createApp(App)

    // 4) Install router & store
    app.use(store)
    app.use(router)

    // 5) Install Bugsnag Vue plugin
    const bugsnagVuePlugin = bugsnagVue as Plugin
    app.use(bugsnagVuePlugin)

    // 6) Install other global directives/mixins

    // 7) Global mixin for Mixpanel events
    app.mixin(mixpanelEvent)

    // 8) Register directives & components
    app.directive('follow-tooltip', followTooltip)
    app.directive('tooltip', VTooltip)

    // 9) Mount the app
    app.mount('#app')

    return { app, router }
  }
)
